import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { limit } from 'firebase/firestore';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { UserService } from 'src/app/services/user/user.service';
import { LoggingService } from 'src/app/services/logging/logging.service';
import {
  Highlight, WhatsNewContext, WhatsNewRelease, toRelease, tryItTarget, unreadCount, visibleReleases,
} from 'src/app/models/whats-new/whats-new.model';

/**
 * What's new in Connect (Vincent, 2026-11-01): the published entries (global `releases`, read in the global
 * project where everyone signs in), filtered for the person's OPEN org with its capabilities (whatsNewContext, in
 * the org's region), an unread count for the rail's dot (`users/{uid}.lastSeenReleaseAt`), and the panel.
 * Content-free usage goes to whatsNewEvent (open / view / try) for the ops dashboard.
 */
@Injectable({ providedIn: 'root' })
export class WhatsNewService {
  private readonly firebase = inject(FirebaseService);
  private readonly organization = inject(OrganizationService);
  private readonly user = inject(UserService);
  private readonly router = inject(Router);
  private readonly logging = inject(LoggingService);

  private readonly releases = signal<WhatsNewRelease[]>([]);
  private readonly context = signal<WhatsNewContext | null>(null);
  private readonly lastSeen = signal<number | null>(null);
  /** Bumped every minute so an entry scheduled for later appears without a reload. */
  private readonly clock = signal(Date.now());

  /** What this person sees in their open org, newest first. */
  public readonly visible = computed(() => visibleReleases(this.releases(), this.context(), 'connect', this.clock()));
  /** Visible entries they have not opened the feed since. Gated or admin-only entries never light the dot for others. */
  public readonly unread = computed(() => unreadCount(this.visible(), this.lastSeen()));
  /** The same count as a plain synchronous stream, for the rail (SidebarNavService rebuilds on it). */
  public readonly unread$ = new BehaviorSubject<number>(0);

  private refresh(): void {
    const n = this.unread();
    if (n !== this.unread$.getValue()) this.unread$.next(n);
  }

  private started = false;

  public start(): void {
    if (this.started) return;
    this.started = true;
    // Best-effort: the feed must never break the rail it sits in (nor a test bed that stubs half of Firebase).
    try {
      this.firebase.globalFirestore.collectionListen(
        'releases',
        [this.firebase.globalFirestore.where('status', '==', 'published'), limit(100)],
        (snap) => {
          this.releases.set(snap.docs.map((d) => toRelease(d.id, d.data())).filter((r): r is WhatsNewRelease => !!r));
          this.refresh();
        },
        (err) => this.logging.error('WhatsNew: releases unavailable', err),
      );
      this.user.lastSeenReleaseAt$.subscribe((at) => {
        this.lastSeen.set(at);
        this.refresh();
      });
      // The org handles are bound to the org's region before the org opens; the context follows the open org.
      combineLatest([this.organization.orgId$, this.firebase.region$]).pipe(
        map(([orgId, region]) => ({ orgId, region })),
        distinctUntilChanged((a, b) => a.orgId === b.orgId && a.region === b.region),
      ).subscribe(({ orgId }) => void this.loadContext(orgId));
      setInterval(() => {
        this.clock.set(Date.now());
        this.refresh();
      }, 60_000);
    } catch (err) {
      this.logging?.error?.('WhatsNew: not started', err);
    }
  }

  private async loadContext(orgId: string | null): Promise<void> {
    this.context.set(null);
    this.refresh();
    if (!orgId) return;
    try {
      const ctx = await this.firebase.functions.call('whatsNewContext', { organizationId: orgId }) as WhatsNewContext;
      if (this.organization.orgId$.getValue() === orgId) {
        this.context.set(ctx);
        this.refresh();
      }
    } catch (err) {
      // No capabilities, no feed: an entry is never shown to an org it may not apply to.
      this.logging.error('WhatsNew: context unavailable', err);
    }
  }

  /** The What's new page opened: everything visible now counts as seen (the rail's dot clears). */
  public opened(): void {
    const newest = this.visible()[0]?.publishedAt ?? 0;
    void this.user.markReleasesSeen(Math.max(Date.now(), newest)).catch((err) => this.logging.error('WhatsNew: seen not saved', err));
    this.track('open');
  }

  public tryIt(release: WhatsNewRelease, highlight: Highlight): boolean {
    const target = tryItTarget(highlight);
    if (!target) return false;
    this.track('try', release.id, highlight.id);
    void this.router.navigate(target.commands, { queryParams: target.queryParams });
    return true;
  }

  public track(type: 'open' | 'view' | 'try', releaseId?: string, highlightId?: string): void {
    void this.firebase.functions.call('whatsNewEvent', {
      type, ...(releaseId ? { releaseId } : {}), ...(highlightId ? { highlightId } : {}),
      organizationId: this.organization.orgId$.getValue(),
    }).catch(() => undefined);
  }
}
